/* 왼쪽 패널 — 사진 · 텍스트 · 요소 · 글꼴 · 디자인(색 테마·글꼴 세트·배경) */
import { useState } from 'react';
import { Button, Checkbox, ItemList, ItemListRow, PhotoGrid, Search, SidePanel as UiSidePanel, Skeleton, Temp_ColorInput, Temp_TileButton } from '@indipia/ui-core';
import {
  BG_SWATCHES, FONTS, FONT_COMBOS, FONT_SETS, PHOTO_CLIPS, SHAPES, STICKERS, THEMES, THEME_LABEL, fontCss, fontOf, roomLabel,
} from '../model/constants';
import { useImageUrl } from '../model/images';
import { newId, type ShowcasePhoto } from '../model/types';
import {
  addAndSelect, applyFontSet, applyTheme, centerOf, change, currentPage, currentThemeKey, ed, nextZ, rememberUndo, selectedElm, setBg, toast, useEd,
} from '../store/editor';
import { loadSites, photoState, spaceOf, srcOf } from '../store/photos';
import { MineToggle, NoPhotos } from './Common';
import { shapeIcon } from './render';
import { chipRow, clipSample, cx, elTile, groupTitle, hint, iconBtn, menuList, photoLabel, rowHint, sec } from './ui';

const PANE = 'flex flex-col gap-sm [&_h3]:m-0 [&_h3]:text-heading-sm';
const textAdd = 'w-full rounded-md bg-surface p-04 text-left';

export type PaneId = 'photo' | 'text' | 'elements' | 'fonts' | 'bg';

/* 새로 넣는 요소의 기본 색 — 문서(페이지) 안 색이라 화면 토큰이 아니다. 내보낸 사진·공개 페이지에 그대로 찍힌다 */
const NEW_TEXT = {
  h: { text: '제목을 입력하세요', fontSize: 40, bold: true, w: 600, h: 64, color: '#1B1F4F' }, // dq-ignore: 문서 글자 색(사진·공개 페이지로 내보냄)
  s: { text: '부제목을 입력하세요', fontSize: 24, bold: false, w: 560, h: 44, color: '#374151' }, // dq-ignore: 문서 글자 색
  b: { text: '내용을 입력하세요. 시공한 공간의 특징과 고객님께 좋아진 점을 적어 보세요.', fontSize: 16, bold: false, w: 520, h: 110, color: '#4B5563' }, // dq-ignore: 문서 글자 색
};
const NEW_SHAPE_BG = '#D9CBB5'; // dq-ignore: 문서 도형 기본 채우기 색
const NEW_LINE_COLOR = '#1F2937'; // dq-ignore: 문서 선 기본 색
const PAGE_WHITE = '#FFFFFF'; // dq-ignore: 문서 페이지 기본 배경

export function Thumb({ src, alt = '' }: { src: string; alt?: string }) {
  const url = useImageUrl(src, true);
  return url ? <img src={url} alt={alt} loading="lazy" /> : null;
}

export function SidePanel({ pane, setPane, open }: { pane: PaneId; setPane: (p: PaneId) => void; open: boolean }) {
  useEd();
  return (
    <UiSidePanel open={open} side="left" label="편집 패널">
      <div className="p-05">
        {pane === 'photo' && <PhotoPane />}
        {pane === 'text' && <TextPane onFonts={() => setPane('fonts')} />}
        {pane === 'elements' && <ElementsPane />}
        {pane === 'fonts' && <FontPane onBack={() => setPane('text')} />}
        {pane === 'bg' && <DesignPane />}
      </div>
    </UiSidePanel>
  );
}

/** 사진 칸을 선택해 두었으면 그 칸의 사진을 바꾸고, 아니면 새로 넣는다 */
function applyPhoto(p: ShowcasePhoto, siteId: number) {
  const sel = selectedElm();
  if (sel && sel.type === 'image') {
    change(sel, 'src', (x) => {
      x.src = srcOf(p); x.photoId = p.photoId; x.externalId = p.externalId || undefined; x.siteId = siteId;
      delete x.aiImgId; delete x.origSrc; delete x.origPhotoId; delete x.origExternalId;
    });
    toast('선택한 사진 칸의 사진을 바꿨어요');
    return;
  }
  addAndSelect({ type: 'image', x: 150, y: 180, w: 420, h: 315, src: srcOf(p), photoId: p.photoId, externalId: p.externalId || undefined, siteId });
}

function PhotoPane() {
  const sites = photoState.sites;
  const head = <div className="flex items-center justify-between gap-sm"><h3>사진</h3><MineToggle /></div>;
  if (!sites) {
    void loadSites();
    return (
      <div className={PANE} aria-busy="true">{head}
        <div className="grid grid-cols-3 gap-sm">{Array.from({ length: 9 }, (_, i) => <Skeleton key={i} variant="block" height={80} />)}</div>
      </div>
    );
  }
  if (!sites.length) {
    return (
      <div className={PANE}>{head}
        {photoState.mine ? <p className={hint}>내가 담당자·관리자인 현장에 올린 사진이 없어요. 내 현장만 보기를 끄면 우리 회사 모든 현장의 사진이 보여요.</p> : <NoPhotos compact />}
      </div>
    );
  }
  return (
    <div className={PANE}>
      {head}
      <p className={hint}>클릭하면 페이지에 추가돼요. 페이지에서 사진 칸을 먼저 선택하면 그 칸의 사진이 바뀌어요.</p>
      {sites.map((s) => {
        const list = s.photos.filter((p) => p.externalId);
        const byId = new Map(list.map((p) => [p.photoId, p]));
        return (
          <section key={s.siteId}>
            <h4 className={groupTitle}>{s.siteName || `현장 ${s.siteId}`}</h4>
            <PhotoGrid className="gap-02 [&_.photo-grid-item]:relative" minColWidth={80} selectedIds={[]}
              items={list.map((p) => ({ id: p.photoId, url: srcOf(p), alt: roomLabel(spaceOf(p)) }))}
              onToggle={(id) => { const p = byId.get(id as number); if (p) applyPhoto(p, s.siteId); }}
              renderThumb={(it) => <><Thumb src={it.url} alt={it.alt} /><span className={cx(photoLabel, 'absolute bottom-01 left-01')}>{it.alt}</span></>} />
          </section>
        );
      })}
    </div>
  );
}

function TextPane({ onFonts }: { onFonts: () => void }) {
  const add = (k: 'h' | 's' | 'b') => {
    const spec = NEW_TEXT[k];
    addAndSelect({ type: 'text', align: 'left', font: FONTS[0].css, ...spec, ...centerOf(spec.w, spec.h) });
  };
  const combo = (i: number) => {
    const c = FONT_COMBOS[i];
    const hh = Math.round(c.hs * 1.5), sh = Math.round(c.ss * 1.8), w = 600, y = Math.round((960 - hh - sh - 10) / 2), x = 60;
    const p = currentPage();
    if (!p) return;
    rememberUndo();
    const z = nextZ();
    p.elements.push({ id: newId(), type: 'text', x, y, w, h: hh, z, text: c.h, fontSize: c.hs, bold: true, align: 'center', color: c.hc, font: fontCss(c.hf), ls: c.ls });
    const sub = { id: newId(), type: 'text' as const, x, y: y + hh + 10, w, h: sh, z: z + 1, text: c.s, fontSize: c.ss, bold: false, align: 'center' as const, color: c.sc, font: fontCss(c.sf) };
    p.elements.push(sub);
    ed.selectedId = sub.id;
    ed.bump();
  };
  return (
    <div className={PANE}>
      <h3>텍스트</h3>
      <p className={hint}>눌러서 편집 화면 가운데에 추가하세요. 글꼴·색·크기는 위쪽 도구 막대에서 바꿔요.</p>
      <Temp_TileButton bordered className={cx(textAdd, 'text-heading-lg font-bold')} onClick={() => add('h')}>제목 추가</Temp_TileButton>
      <Temp_TileButton bordered className={cx(textAdd, 'text-heading-sm')} onClick={() => add('s')}>부제목 추가</Temp_TileButton>
      <Temp_TileButton bordered className={cx(textAdd, 'text-body-sm')} onClick={() => add('b')}>본문 추가</Temp_TileButton>
      <h3 className={sec}>글꼴 조합</h3>
      <div className="grid grid-cols-2 gap-sm">
        {FONT_COMBOS.map((c, i) => (
          <Temp_TileButton key={i} bordered className="flex min-h-11 flex-col items-center justify-center gap-xs overflow-hidden break-keep rounded-md bg-[var(--gray-0)] p-03 text-center [&>strong]:text-heading-sm [&>strong]:font-bold [&>strong]:leading-tight [&>span]:text-caption [&>span]:leading-snug" aria-label={`글꼴 조합 ${c.h} · ${c.s}`} onClick={() => combo(i)}>
            {/* 글꼴·색 견본 — 조합 데이터 그대로 */}
            <strong style={{ fontFamily: fontCss(c.hf), color: c.hc, letterSpacing: c.ls }}>{c.h}</strong>
            <span style={{ fontFamily: fontCss(c.sf), color: c.sc }}>{c.s}</span>
          </Temp_TileButton>
        ))}
      </div>
      <h3 className={sec}>글꼴 둘러보기</h3>
      <Button variant="etc" className="w-full" onClick={onFonts}>한글 글꼴 {FONTS.length}종 보기 ›</Button>
    </div>
  );
}

function FontPane({ onBack }: { onBack: () => void }) {
  const [q, setQ] = useState('');
  const elm = selectedElm();
  const cur = elm && elm.type === 'text' ? fontOf(elm.font).name : null;
  const list = FONTS.filter((f) => !q || f.name.includes(q) || f.tag.includes(q));
  return (
    <div className={PANE}>
      <div className="flex items-center gap-xs"><Button size="sm" variant="ghost" className={iconBtn} aria-label="텍스트로 돌아가기" onClick={onBack}>‹</Button><h3>글꼴</h3></div>
      <Search size="sm" placeholder="글꼴 검색 (예: 명조, 손글씨)" aria-label="글꼴 검색" value={q} onChange={(v) => setQ(v.trim())} autoFocus />
      {!(elm && elm.type === 'text') && <p className={hint}>페이지에서 텍스트를 먼저 선택하면 그 글꼴이 바뀌어요.</p>}
      {(['고딕', '명조', '제목', '손글씨'] as const).map((g) => {
        const fs = list.filter((f) => f.tag === g);
        if (!fs.length) return null;
        return (
          <section key={g} className={PANE}>
            <h4 className={groupTitle}>{g}</h4>
            <ItemList className={menuList}>
              {fs.map((f) => (
                <ItemListRow key={f.name} aria-current={cur === f.name || undefined} className={cur === f.name ? '[&_.item-list-title]:text-group' : undefined}
                  onClick={() => {
                    const e = selectedElm();
                    if (!e || e.type !== 'text') { toast('페이지에서 텍스트를 먼저 선택해 주세요'); return; }
                    change(e, 'font', (x) => (x.font = f.css));
                  }}
                  title={<span className="text-heading-sm" style={{ fontFamily: f.css }}>{f.name}</span>}
                  hint={<span className={rowHint} style={{ fontFamily: f.css }}>우리집 시공사례 · 가나다 Abc 123</span>}
                  trailing={cur === f.name ? <span className="font-semibold text-group" aria-hidden>✓</span> : undefined} />
              ))}
            </ItemList>
          </section>
        );
      })}
    </div>
  );
}

function ElementsPane() {
  return (
    <div className={PANE}>
      <h3>도형</h3>
      <div className="grid grid-cols-4 gap-sm">
        {Object.entries(SHAPES).filter(([k]) => k !== 'line').map(([k, s]) => (
          <Temp_TileButton key={k} className={elTile()} title={s.label} aria-label={s.label} onClick={() => {
            const w = k === 'arrow' ? 260 : 220, h = k === 'arrow' ? 140 : k === 'rect' || k === 'round' ? 160 : 220;
            addAndSelect({ type: 'shape', kind: k, bg: NEW_SHAPE_BG, w, h, ...centerOf(w, h) });
          }}>{shapeIcon(k, 'var(--gray-400)')}</Temp_TileButton>
        ))}
      </div>
      <h3 className={sec}>선</h3>
      <div className="grid grid-cols-3 gap-sm">
        {([['solid', '실선'], ['dashed', '점선'], ['dotted', '동그란 점선'], ['arrow', '화살표'], ['both', '양쪽 화살표']] as const).map(([t, label]) => (
          <Temp_TileButton key={t} className={elTile(false, true)} title={label} aria-label={label} onClick={() => {
            const w = 320, h = 24;
            addAndSelect({ type: 'shape', kind: 'line', bg: 'transparent', stroke: NEW_LINE_COLOR, sw: 3, dash: t === 'dashed' || t === 'dotted' ? t : 'solid',
              arrow: t === 'arrow' ? 'end' : t === 'both' ? 'both' : 'none', w, h, ...centerOf(w, h) });
          }}>
            <svg viewBox="0 0 100 40" aria-hidden>
              <line x1={t === 'both' ? 20 : 6} y1="20" x2={t === 'arrow' || t === 'both' ? 80 : 94} y2="20" stroke="var(--gray-500)" strokeWidth={t === 'dotted' ? 5 : 4}
                strokeDasharray={t === 'dashed' ? '12 8' : t === 'dotted' ? '0 10' : undefined} strokeLinecap={t === 'dotted' ? 'round' : undefined} />
              {(t === 'arrow' || t === 'both') && <path d={'M80 10 L96 20 L80 30 Z' + (t === 'both' ? ' M20 10 L4 20 L20 30 Z' : '')} fill="var(--gray-500)" />}
            </svg>
          </Temp_TileButton>
        ))}
      </div>
      <h3 className={sec}>사진 모양</h3>
      <p className={hint}>페이지에서 사진을 선택한 뒤 눌러 보세요.</p>
      <div className="grid grid-cols-4 gap-sm">
        {Object.entries(PHOTO_CLIPS).map(([k, c]) => (
          <Temp_TileButton key={k} className={elTile()} title={c.label} aria-label={c.label} onClick={() => {
            const e = selectedElm();
            if (!e || e.type !== 'image') { toast('페이지에서 사진을 먼저 선택해 주세요'); return; }
            change(e, 'clip', (x) => { x.clip = k; if (k === 'circle' && Math.abs(x.w - x.h) > 4) { const s = Math.min(x.w, x.h); x.w = s; x.h = s; } });
          }}><i className={clipSample(k)} /></Temp_TileButton>
        ))}
      </div>
      <h3 className={sec}>시공사례 스티커</h3>
      <div className={chipRow}>
        {STICKERS.map((s, i) => (
          <Temp_TileButton key={i} className="rounded-md bg-surface p-03 hover:bg-surface-raised" aria-label={`${s.label} 스티커`} onClick={() => addAndSelect({ type: 'text', align: 'center', vcenter: true, font: FONTS[0].css, pad: s.el.pad || '0 14px', ...s.el, ...centerOf(s.el.w, s.el.h) })}>
            {/* 스티커 견본 — 스티커 데이터(문서 색) 그대로 */}
            <span className={cx('inline-flex h-06 min-w-06 items-center justify-center whitespace-nowrap px-03 text-caption font-bold', s.el.bg === PAGE_WHITE && 'shadow-[inset_0_0_0_1px_var(--gray-200)]')} style={{ background: s.el.bg, color: s.el.color, borderRadius: Math.min(s.el.radius, 16), letterSpacing: s.el.ls }}>{s.el.text}</span>
          </Temp_TileButton>
        ))}
      </div>
    </div>
  );
}

function DesignPane() {
  const cur = currentThemeKey();
  const bgNow = currentPage()?.bg || PAGE_WHITE;
  const [all, setAll] = useState(false);
  const norm = (c: string) => c.replace(/\s+/g, '').toLowerCase();
  return (
    <div className={PANE}>
      <h3>색 테마</h3>
      <p className={hint}>누르면 모든 페이지의 배경·제목·본문·포인트 색이 한 번에 바뀌어요. 사진과 직접 넣은 색은 그대로예요.</p>
      <div className="grid grid-cols-3 gap-sm">
        {Object.keys(THEMES).map((k) => {
          const t = THEMES[k];
          return (
            <Temp_TileButton key={k} selected={k === cur} className="flex flex-col items-center gap-xs rounded-md p-02 aria-pressed:bg-group-subtle" title={THEME_LABEL[k]} onClick={() => applyTheme(k)}>
              {/* 테마 견본 — 테마 데이터(문서 색) 그대로 */}
              <span className="relative flex aspect-[3/4] w-full flex-col justify-end gap-02 overflow-hidden rounded-sm border border-[var(--alpha-black-100)] p-03" style={{ background: t.bg }} aria-hidden>
                <i className="absolute inset-x-0 top-0 h-[34%]" style={{ background: t.alt }} />
                <b className="relative text-left text-body-sm leading-none" style={{ color: t.head, fontFamily: t.headFont || 'inherit' }}>가나다</b>
                <span className="relative block h-02 rounded-xs opacity-50" style={{ background: t.text }} /><span className="relative block h-02 w-3/5 rounded-xs opacity-50" style={{ background: t.text }} />
                <em className="absolute right-03 top-[calc(34%+var(--spacing-03))] size-04 rounded-full" style={{ background: t.accent }} />
              </span>
              <small className="max-w-full truncate text-caption text-text-secondary">{THEME_LABEL[k]}</small>
            </Temp_TileButton>
          );
        })}
      </div>
      <h3 className={sec}>글꼴 세트</h3>
      <p className={hint}>제목(공간 이름·표지 제목 같은 큰 글)과 나머지 글의 글꼴을 모든 페이지에 한 번에.</p>
      <ItemList className={menuList}>
        {FONT_SETS.map((s, i) => (
          <ItemListRow key={i} onClick={() => applyFontSet(i)}
            title={<span className="text-heading-sm" style={{ fontFamily: fontCss(s.head) }}>우리집 시공사례</span>}
            hint={<span className={rowHint} style={{ fontFamily: fontCss(s.body) }}>{s.name} — 거실을 넓고 밝게 정리했습니다</span>} />
        ))}
      </ItemList>
      <h3 className={sec}>배경색</h3>
      <div className={chipRow} role="group" aria-label="배경색">
        {BG_SWATCHES.map((c) => (
          <Temp_TileButton key={c} selected={norm(c) === norm(bgNow)} bordered className="size-[var(--control-height-base)] rounded-md aria-pressed:outline aria-pressed:outline-2 aria-pressed:outline-offset-1 aria-pressed:outline-group" style={{ background: c }} title={c} aria-label={`배경색 ${c}`} onClick={() => setBg(c, all)} />
        ))}
        <span className="relative inline-flex size-[var(--control-height-base)] overflow-hidden rounded-md border border-border bg-[conic-gradient(var(--status-danger-500),var(--status-warning-500),var(--status-success-500),var(--status-info-500),var(--indipia-purple-500),var(--status-danger-500))]" title="직접 고르기">
          <Temp_ColorInput className="size-full opacity-0" label="배경색 직접 고르기" value={/^#[0-9a-f]{6}$/i.test(bgNow) ? bgNow : '#ffffff' /* dq-ignore: 색 선택기 초기값(문서 색) */} onChange={(v) => setBg(v, all)} />
        </span>
      </div>
      <Checkbox label="모든 페이지에 적용" checked={all} onChange={(e) => setAll(e.target.checked)} />
    </div>
  );
}
